import React from "react";
import L from "leaflet";
import { createRoot, Root } from "react-dom/client";
import { useLayer } from "./LayerContext";
import { layerIDs, LayerControlDefinition } from "./layerIDs";

type ILayerElement = {
  id: string,
  label: string,
  completed: boolean
}

type LayerStatus = Record<string, { loading?: boolean } | undefined>;

type PointControlsContentProps = {
  layersList: ILayerElement[];
  layerStatus: LayerStatus;
  addLayer: (id: string) => void;
  removeLayer: (id: string) => void;
};

type MapPointControlsProps = {
  map: L.Map | null;
};

const pointLayers = layerIDs.filter((layer) => layer.group === "point");

const PointControlsContent: React.FC<PointControlsContentProps> = ({
  layersList,
  layerStatus,
  addLayer,
  removeLayer,
}) => {
  const activeLayerIds = React.useMemo(
    () => layersList.map((layer: ILayerElement) => layer.id),
    [layersList]
  );

  return (
    <div className="leaflet-point-controls__list">
      {pointLayers.map((layer: LayerControlDefinition) => {
        const isActive = activeLayerIds.includes(layer.id);
        const isLoading = Boolean(layerStatus?.[layer.id]?.loading);
        const buttonClassName = [
          "leaflet-point-control__button",
          isActive ? "is-active" : "",
          !isActive && !isLoading ? "is-inactive" : "",
          isLoading ? "leaflet-disabled" : "",
        ].filter(Boolean).join(" ");

        return (
          <div className="leaflet-control leaflet-point-control" key={layer.id}>
            <a
              className={buttonClassName}
              href="/#"
              title={layer.name}
              aria-label={layer.name}
              aria-disabled={isLoading}
              aria-pressed={isActive}
              role="button"
              onClick={(event) => {
                event.preventDefault();

                if (isLoading) {
                  event.currentTarget.blur();
                  return;
                }

                if (isActive) {
                  removeLayer(layer.id);
                } else {
                  addLayer(layer.id);
                }

                event.currentTarget.blur();
              }}
            >
              {isLoading ? (
                <span className="leaflet-point-control__loader" aria-hidden="true"></span>
              ) : (
                <img className="leaflet-point-control__icon" src={layer.icon} alt="" />
              )}
              <span className="leaflet-point-control__label" aria-hidden="true">
                {layer.name}
              </span>
            </a>
          </div>
        );
      })}
    </div>
  );
};

const MapPointControls: React.FC<MapPointControlsProps> = ({ map }) => {
  const { layersList, addLayer, removeLayer, layerStatus } = useLayer();
  const controlRef = React.useRef<L.Control | null>(null);
  const rootRef = React.useRef<Root | null>(null);

  React.useEffect(() => {
    if (!map || controlRef.current) {
      return;
    }

    const CustomPointControl = L.Control.extend({
      options: {
        position: "topleft",
      },
      onAdd() {
        const container = L.DomUtil.create("div", "leaflet-point-controls");

        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        rootRef.current = createRoot(container);

        return container;
      },
      onRemove() {
        rootRef.current?.unmount();
        rootRef.current = null;
      },
    });

    const pointControl = new CustomPointControl();
    pointControl.addTo(map);
    controlRef.current = pointControl;

    return () => {
      pointControl.remove();
      controlRef.current = null;
    };
  }, [map]);

  React.useEffect(() => {
    if (!rootRef.current) {
      return;
    }

    rootRef.current.render(
      <PointControlsContent
        layersList={layersList}
        layerStatus={layerStatus}
        addLayer={addLayer}
        removeLayer={removeLayer}
      />
    );
  }, [addLayer, layerStatus, layersList, removeLayer]);

  return null;
};

export default MapPointControls;
